import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { toKey } from "../components/editModalFunctions";

type FormLike = {
  customer?: string;
  customerKey?: string;
  articleNumber?: string;
};

type CustomerOpt = { key: string; name: string };

type Params<TForm extends FormLike> = {
  db: Firestore;
  auth: Auth;
  form: TForm;
  setForm: Dispatch<SetStateAction<TForm>>;
  customerListOpts: CustomerOpt[];
};

export function useArticlesByCustomer<TForm extends FormLike>({
  db,
  auth,
  form,
  setForm,
  customerListOpts,
}: Params<TForm>) {
  const [articleList, setArticleList] = useState<string[]>([]);
  const [newArticle, setNewArticle] = useState("");
  const [showNewArticleInput, setShowNewArticleInput] = useState(false);

  const fetchArticlesFor = useCallback(async (customerKeyOrName: string): Promise<void> => {
    const k = String(customerKeyOrName || "").trim();
    if (!k) { setArticleList([]); return; }

    const match = Array.isArray(customerListOpts) ? customerListOpts.find(o => o.key === k) : undefined;
    const displayName = match?.name || k;

    try {
      const byIdSnap = await getDocs(query(collection(db, "articles"), where("customerId", "==", k)));
      let names = byIdSnap.docs.map(d => String((d.data() as any).name || "")).filter(Boolean);

      if (names.length === 0) {
        const byNameSnap = await getDocs(query(collection(db, "articles"), where("customer", "==", displayName)));
        names = byNameSnap.docs.map(d => String((d.data() as any).name || "")).filter(Boolean);
      }

      const uniq = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }));
      setArticleList(uniq);
    } catch (e: any) {
      console.error("Kunde inte hämta artiklar:", e?.message || e);
      setArticleList([]);
    }
  }, [customerListOpts, db]);

  const handleNewArticleAdd = useCallback(async (): Promise<void> => {
    const trimmed = newArticle.trim();
    if (!trimmed || !form.customerKey) return;
    const key = toKey(trimmed);

    if (articleList.some(a => toKey(a) === key)) {
      alert("Modell/artikel finns redan för kunden.");
      setForm(p => ({ ...p, articleNumber: articleList.find(a => toKey(a) === key)! }));
      setNewArticle("");
      setShowNewArticleInput(false);
      return;
    }

    try {
      const all = await getDocs(query(collection(db, "articles")));
      const hit = all.docs.map(d => d.data() as any).find(r => r?.name && toKey(r.name) === key);

      if (hit) {
        const other = String(hit.customer || "");
        if (toKey(other) !== toKey(form.customer)) {
          if (!window.confirm(`Artikeln finns redan hos "${other}". Vill du spara ändå för "${form.customer}"?`)) return;
        } else {
          alert("Modell/artikel finns redan för kunden.");
          setForm(p => ({ ...p, articleNumber: hit.name as string }));
          setNewArticle("");
          setShowNewArticleInput(false);
          await fetchArticlesFor(form.customerKey);
          return;
        }
      }

      await addDoc(collection(db, "articles"), {
        name: trimmed,
        customer: form.customer || "",
        customerId: form.customerKey || "",
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
      });

      await fetchArticlesFor(form.customerKey);
      setForm(p => ({ ...p, articleNumber: trimmed }));
      setNewArticle("");
      setShowNewArticleInput(false);
    } catch (e: any) {
      alert("Fel vid tillägg av artikel");
    }
  }, [articleList, auth, db, fetchArticlesFor, form.customer, form.customerKey, newArticle, setForm]);

  return {
    articleList,
    setArticleList,
    newArticle,
    setNewArticle,
    showNewArticleInput,
    setShowNewArticleInput,
    fetchArticlesFor,
    handleNewArticleAdd,
  };
}
